/**
 * The ONE seam between this app and `@most/api-client`.
 *
 * The shared client is authored in parallel by another agent, so its exact export surface is not
 * yet pinned. Every network call in the producer cabinet goes through the tiny `request()` below,
 * which means when the shared client lands, adapting to it is a change to THIS FILE and nothing
 * else — not a sweep through forty call sites.
 *
 * Contract-mandated behaviours that live here (docs/05, plans §9):
 *   • `credentials: 'include'` — auth is the HttpOnly `most_session` cookie. No JWT, no
 *     Authorization header. All four frontends share one origin, so CSRF is an Origin check
 *     server-side and we need no token plumbing.
 *   • RFC 9457 `application/problem+json` → thrown `ApiError`. Callers switch on the stable
 *     `type` SUFFIX and translate it themselves; `detail` is English and is NEVER rendered.
 *   • 401 → a single global handler (see `queryClient.ts`). Three copies of the 401 handler
 *     would be three different bugs.
 */
import type { Problem } from './contract';

export const API_BASE = '/api/v1';

/**
 * A thrown problem+json response. `type` is a URL like
 * `https://most.example/problems/validation-failed`; UI code should match on `.suffix`, which is
 * the stable, translatable tail — matching on the full URL breaks if the domain ever moves.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly problem: Problem;

  constructor(problem: Problem) {
    // `message` is for logs and stack traces only. Never render it: `detail` is English by
    // contract, and the user's locale may not be.
    super(problem.detail ?? problem.title ?? `HTTP ${problem.status}`);
    this.name = 'ApiError';
    this.status = problem.status;
    this.problem = problem;
  }

  /** The stable tail of the problem `type` URL, e.g. `validation-failed`. */
  get suffix(): string {
    const t = this.problem.type ?? '';
    const tail = t.split('/').pop();
    return tail && tail !== 'about:blank' ? tail : `http-${this.status}`;
  }

  /** Field-level validation errors, keyed by field name, as stable translatable codes. */
  get fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const e of this.problem.errors ?? []) out[e.field] = e.code;
    return out;
  }

  get isUnauthenticated(): boolean {
    return this.status === 401;
  }
}

/** Raised when the network itself failed — offline, DNS, TLS. Distinct from a server problem. */
export class NetworkError extends Error {
  constructor(cause: unknown) {
    super('network request failed');
    this.name = 'NetworkError';
    this.cause = cause;
  }
}

/** Listener invoked exactly once per 401, wired up in `queryClient.ts`. */
type UnauthorizedHandler = () => void;
let onUnauthorized: UnauthorizedHandler = () => {};
export function setUnauthorizedHandler(fn: UnauthorizedHandler): void {
  onUnauthorized = fn;
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /** JSON body. Mutually exclusive with `formData`. */
  body?: unknown;
  /** Multipart body for uploads (media, certificates). Never set Content-Type by hand for this. */
  formData?: FormData;
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

async function toProblem(res: Response): Promise<Problem> {
  // The server SHOULD send problem+json, but a proxy 502 or a truncated response will not.
  // Synthesize a well-formed Problem either way so callers always get the same shape.
  try {
    const ct = res.headers.get('content-type') ?? '';
    if (ct.includes('json')) {
      const body = (await res.json()) as Partial<Problem>;
      return {
        type: body.type ?? 'about:blank',
        title: body.title ?? res.statusText,
        status: body.status ?? res.status,
        detail: body.detail,
        errors: body.errors,
        retry_after_seconds: body.retry_after_seconds,
      };
    }
  } catch {
    // fall through to the synthetic problem
  }
  return { type: 'about:blank', title: res.statusText, status: res.status };
}

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, formData, signal, headers = {} } = opts;

  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method,
      // The session cookie is the whole auth story. Without this, every call is a 401.
      credentials: 'include',
      signal,
      headers: formData
        ? headers
        : { ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}), ...headers },
      body: formData ?? (body !== undefined ? JSON.stringify(body) : undefined),
    });
  } catch (cause) {
    if (signal?.aborted) throw cause;
    throw new NetworkError(cause);
  }

  if (res.status === 401) {
    onUnauthorized();
    throw new ApiError(await toProblem(res));
  }

  if (!res.ok) throw new ApiError(await toProblem(res));

  // 204 No Content — and any empty body — must not go through res.json().
  if (res.status === 204 || res.headers.get('content-length') === '0') {
    return undefined as T;
  }

  return (await res.json()) as T;
}

/** XHR-based upload: `fetch` still cannot report request-body progress, and PRO-4 needs a bar. */
export function upload<T>(
  path: string,
  formData: FormData,
  onProgress?: (pct: number) => void,
  signal?: AbortSignal,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_BASE}${path}`, true);
    xhr.withCredentials = true;

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };

    xhr.onload = () => {
      const raw = xhr.responseText;
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(raw ? (JSON.parse(raw) as T) : (undefined as T));
        } catch (err) {
          reject(new NetworkError(err));
        }
        return;
      }
      if (xhr.status === 401) onUnauthorized();
      let problem: Problem = { type: 'about:blank', title: xhr.statusText, status: xhr.status };
      try {
        const parsed = JSON.parse(raw) as Partial<Problem>;
        problem = { ...problem, ...parsed, status: parsed.status ?? xhr.status };
      } catch {
        /* non-JSON error body (proxy error page) — keep the synthetic problem */
      }
      reject(new ApiError(problem));
    };

    xhr.onerror = () => reject(new NetworkError(new Error('xhr error')));
    xhr.onabort = () => reject(new DOMException('aborted', 'AbortError'));

    signal?.addEventListener('abort', () => xhr.abort());
    xhr.send(formData);
  });
}
